import { useLocalStorage, usePendingAsync } from '@/component/hook';
import { Result, Spin } from 'antd';
import { compress, decompress } from 'lz-string';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import client from '../utils/http-request';

const FullScreenDiv = styled.div`
    width: 100%;
    height: 100%;
    position: fixed;
    display: flex;
    justify-content: center;
    align-items: center;
`

interface User {
    domainId: string;
    liner: string;
    email: string;
    firstName: string;
    username: string;
    lastName: string;
    scopes?: string[];
    permissions?: any;
    [key: string]: any;
}

interface Env {
    linerGroup: string;
    [key: string]: any;
}

interface InternalAuth {
    user?: User;
    env: Env;
    loading: boolean;
}

interface Auth {
    user: User;
    env: Env;
}

const globalRequestUser = async () => {
    const res: any = await client.request({
        method: 'get',
        withCredentials: true,
        url: '/gateway/api/frm/sso/userinfo'
    });
    return {
        ...res?.data?.data
    };
};

const requestEnvironmentVariables = async () => {
    return client.request({
        method: 'get',
        url: `/environment-variables`,
    }).then(res => res.data as Env);
};

const AuthContext = createContext<Auth>((null as unknown) as Auth);

const useAuth = () => useContext(AuthContext);
const roeUser = 'roe_uif';

const AuthProvider: React.FC<any> = ({ children }) => {
    const [userStr, setUserStr] = useLocalStorage(roeUser);
    const user = userStr ? JSON.parse(decompress(userStr)) : undefined;
    const setUser = (obj) => {
        setUserStr(compress(JSON.stringify(obj)));
    };
    const [loading, setLoading] = useState(true);
    const [env, setEnv] = useState({} as Env);

    const requestPendingUser = usePendingAsync(globalRequestUser);
    const requestPendingEnvironment = usePendingAsync(requestEnvironmentVariables);

    useEffect(() => {
        // requestPendingUser first to refresh access token if expired
        requestPendingUser().then(userData => {
            setLoading(true);
            requestPendingEnvironment()
                .then((environment) => {
                    setUser(userData);
                    setEnv(environment);
                })
                .finally(() => setLoading(false));
        });
    }, []);

    const auth: InternalAuth = useMemo(() => ({ user, env, loading }), [user, loading]);

    if (auth.loading) {
        return (
            <FullScreenDiv>
                <Spin size='large' />
            </FullScreenDiv>
        )
    }

    const hasAccessRight = (auth?.user?.scopes?.filter(s => ['roe.support', 'roe.vendorUser'].includes(s)) || []).length > 0;
    if (!auth || !auth.user || hasAccessRight) {
        return (
            <AuthContext.Provider value={{ user: auth.user!, env: auth.env }}>
                {children}
            </AuthContext.Provider>
        );
    }

    const msg = auth?.user ? 'Sorry, you are not authorized to access this page.' : 'You are not authorized.';
    return (
        <FullScreenDiv>
            <Result
                status='403'
                title='403'
                subTitle={msg}
            />
        </FullScreenDiv>
    );
};

export default AuthProvider;
export {
    useAuth
};
export type {
    Auth,
    User,
    Env,
};

